<template>
  <div class="wrapper-full" id="calibration">
    <transition name="slide-fade">
      <ImgBox
        class="imgbox"
        v-if="isLargImgVisible"
        :url="largeImgUrl"
        :dispW="1520"
        :dispH="1140"
        :centOffset="centOffset"
        :style="previewStyle"
      />
    </transition>
    <el-container style="line-height: 1; text-align: left">
      <el-main :style="`height:${dispH}px;position:relative;`">
        <div class="eyetype-symbol" :style="symbolStyle" v-html="symbolContent"></div>
        <div class="eyetype-symbol" :style="ctOffsetStyle">
          <span :style="`color:${ctOffsetStr.x.color}`">
            Offset X:&nbsp;{{ ctOffsetStr.x.label }}
          </span>
          <br />
          <span :style="`color:${ctOffsetStr.y.color}`">
            Offset Y:&nbsp;{{ ctOffsetStr.y.label }}
          </span>
          <br />
          <span :style="`color:${ctOffsetStr.z.color}`">
            Offset Z:&nbsp;{{ ctOffsetStr.z.label }}
          </span>
        </div>
        <div class="eyetype-symbol" :style="lampStyle">
          <lamp-settings
            class="form-text"
            :currentMode="currentModeKey"
            :showTitle="false"
            :showSplit="false"
            :simple="true"
            @change="setCaptureMode"
          ></lamp-settings>
        </div>
        <canvas
          style="z-index: 11"
          id="camCalibration"
          :width="videoSize.width"
          :height="videoSize.height"
          class="cam-window black"
          :style="previewStyle"
          @wheel="zoomPreview"
        ></canvas>
        <canvas
          style="z-index: 12"
          id="indicatrixCalibDrawer"
          class="cam-window"
          :width="videoSize.width"
          :height="videoSize.height"
          :style="previewStyle1"
          @wheel="zoomPreview"
        ></canvas>
      </el-main>
      <el-aside width="400px" class="aside">
        <div class="capture-aside">
          <div>
            <el-radio-group
              v-model="shotMode"
              text-color="#00ff00"
              :disabled="isCalRing || isLargImgVisible"
            >
              <el-radio label="far" text-color="#00ff00" @change="modeChanged">
                {{ $t("langcfg.txtFar") }}
              </el-radio>
              <el-radio label="near" text-color="#00ff00" @change="modeChanged">
                {{ $t("langcfg.txtNear") }}
              </el-radio>
            </el-radio-group>
          </div>
          <div>
            <!-- <split-h class="split-h" :splitH="10"></split-h> -->
            <pre
              style="font-size: 30px; font-family: &quot;Avenir&quot;, Helvetica, Arial, sans-serif"
              >{{ $t("langcfg.txtRingDia") }}:</pre
            >
            <split-h class="split-h" :splitH="5"></split-h>
            <el-input-number
              :precision="2"
              :step="0.01"
              :max="8.5"
              :min="7.5"
              @focus="handleClick('inputRingR')"
              v-model="inputRingR"
            ></el-input-number>
            <span style="font-size: 30px">(7.5~8.5)</span>
            <split-h class="split-h" :splitH="10"></split-h>
            <!-- <pre style="font-size: 30px;font-family: 'Avenir', Helvetica, Arial, sans-serif;">{{ $t('langcfg.txtCompensateValue') }}:</pre>
            <split-h class="split-h" :splitH="5"></split-h>
            <el-input-number
              :precision="2"
              :step="0.01"
              :max="10"
              :min="-10"
              @focus="handleClick('inputCompensate')"
              v-model="inputCompensate"
            ></el-input-number> -->
            <!-- <split-h class="split-h" :splitH="15"></split-h> -->
            <el-checkbox v-model="ctAutoUpDown" @change="ctAutoUpDownChanged">
              {{ $t("langcfg.txtAutoUpDown") }}</el-checkbox
            >
            <split-h class="split-h" :splitH="15"></split-h>
            <el-button
              class="border-normal"
              :disabled="isCalRing || isLargImgVisible"
              style="line-height: 2; width: 90%"
              @click="calRings"
              >{{ $t("langcfg.txtCalibration") }}</el-button
            >
            <split-h class="split-h" :splitH="5"></split-h>
            <el-button
              class="border-normal"
              :disabled="isCalRing || isLargImgVisible"
              style="line-height: 1.5; width: 90%; white-space: inherit"
              @click="onRestoreClick"
            >
              {{ $t("langcfg.txtRestore") }}
            </el-button>
            <split-h class="split-h" :splitH="10"></split-h>
          </div>
          <div style="position: relative">
            <AxisIndication
              style="border: 1px solid gray"
              :dispW="340"
              :dispH="280"
              :rightsider="rightsider"
              :axisFlat="axisFlat"
              :axisSteep="axisSteep"
              :url="calibImgUrl"
              :origImgPath="origImgPath"
            ></AxisIndication>
          </div>
          <split-h class="split-h" :splitH="5"></split-h>
          <div class="aside-content">
            <LiftControl :isCompact="true" />
          </div>
          <el-button class="border-normal btn-exit" @click="exit">
            {{ $t("langcfg.txtExit") }}
          </el-button>
        </div>
      </el-aside>
    </el-container>
  </div>
</template>

<script>
import { Size, Message } from "../../mixins";
import { PAGES } from "../../shared/consts";
import {
  canConvertNumber,
  getSystemTime,
  isDefined,
  isNumNotNanOrInfinite
} from "../../shared/utils";
import GLRenderer from "../../mainInterface/js/GLRenderer";
import { getOffsetShown, getCTCenterOffsetInfo } from "../js/CalCenter";
import PreviewIndicatrix from "../../mainInterface/js/PreviewIndicatrix";
import DrawCTCal from "../../mainInterface/js/DrawCTCal";
import EVENTS from "../../bus";
import { calRings as doCalRings } from "../js/CalCase";
import LampSettings from "../../mainInterface/components/settings/LampSettings.vue";
import LiftControl from "../../mainInterface/components/LiftControl.vue";
import AxisIndication from "./AxisIndication.vue";
import fileSys from "../../fileoprators/fileSystem";
import { FILES } from "../shared";
import { TOPOGRAPHYEVENTS } from "../shared/events";
import ImgBox from "./ImgBox.vue";
import { saveShotInfo } from "../../settings";
import { mapGetters } from "vuex";
const path = window.require("path");

const ASIDEWIDTH = 400;
let glRenderer; // = new GLRenderer()
let _self;
let canvasDraw, camDisplay, previewIndicatrix;
let centerpos = { x: 512, y: 384 };
let autoCnt = 0;

export default {
  name: "Calibration",
  components: {
    LampSettings,
    LiftControl,
    AxisIndication,
    ImgBox
  },
  props: {
    rightsider: {
      type: Object,
      default: () => {
        return {
          visible: true,
          isMapBoolen: false,
          isScaleBoolen: false,
          isImageBoolen: true,
          isDiff: false
        };
      }
    }
  },
  data() {
    return {
      eyeType: "OS",
      videoSize: {
        width: 1024,
        height: 768
      },
      ctCaledOffset: {},
      shotMode: "far",
      isCameraReady: false,
      mode: PAGES.topography,
      inputRingR: 7.99,
      isCalRing: false,
      videoZoom: 1,
      axisSteep: 60,
      axisFlat: 30,
      calibImgUrl: "",
      isLargImgVisible: false,
      largeImgUrl: "",
      origImgPath: "",
      centOffset: { x: {}, y: {}, z: {} },
      imgDir: fileSys.getCalibPath("far"),
      ctAutoUpDown: true,
      inputCompensate: -0.07,
      showingLargeImg: false
    };
  },
  created() {},
  mounted() {
    if (isDefined(window.calibSettings.Ring) && isDefined(window.calibSettings.Ring.compensate)) {
      this.inputCompensate = window.calibSettings.Ring.compensate;
    }
    this.resetImgUrl();
    if (!global.glRenderer) {
      global.glRenderer = new GLRenderer();
    }
    glRenderer = global.glRenderer;

    document.addEventListener("keydown", this.onkeydown);
    canvasDraw = document.getElementById("indicatrixCalibDrawer");
    camDisplay = document.getElementById("camCalibration");
    this.initCamera();
    glRenderer.setZoom(0, 0, this.videoZoom);
    previewIndicatrix = new PreviewIndicatrix(canvasDraw, centerpos);
    this.ctAutoUpDown = window.ctShotInfo.autoMove.updown;
    this.$nextTick(() => {
      let x = parseFloat(window.calibSettings.CenterPoint.x);
      let y = parseFloat(window.calibSettings.CenterPoint.y);
      this.centerPoint = { x, y };
      this.drawCT();
    });
    this.ensureBackup();
    this.addShowLargeImg();
  },
  mixins: [Size, Message],
  methods: {
    resetImgUrl() {
      this.imgDir = fileSys.getCalibPath(this.shotMode);
      if (!fileSys.isFileExists(this.imgDir)) {
        fileSys.mkdirSync(this.imgDir);
      }
      this.origImgPath = this.calibImgUrl = "";
      this.$nextTick(() => {
        this.origImgPath = fileSys.combinePath(this.imgDir, FILES.originImage);
        this.calibImgUrl = `file://${this.origImgPath}?t=${Math.random()}`;
      });
    },
    addShowLargeImg() {
      this.$bus.$on(TOPOGRAPHYEVENTS.showLargeImg, (showEdge, paramChanged, ctOffset) => {
        this.showingLargeImg = true;
        this.centOffset = ctOffset;
        let edgeImgPath = fileSys.combinePath(this.imgDir, FILES.calibImage);
        let originImgPath = fileSys.combinePath(this.imgDir, FILES.originImage);
        let url = "file://" + (showEdge ? edgeImgPath : originImgPath);
        let edgeImgExists = fileSys.isFileExists(edgeImgPath);
        if (showEdge && !edgeImgExists) {
          this.largeImgUrl = `file://${originImgPath}?${Math.random()}`;
        } else {
          this.largeImgUrl = `${url}?${Math.random()}`;
        }
        // console.log('this.largeImgUrl---', this.largeImgUrl)
        if (!paramChanged) {
          this.isLargImgVisible = !this.isLargImgVisible;
        }
        this.showingLargeImg = false;
      });
    },
    handleClick(key) {
      let self = this;
      this.$bus.$emit(EVENTS.spreadKeyBoard, self[key].toString(), (value) => {
        if (canConvertNumber(value)) {
          self[key] = parseFloat(value);
        }
      });
    },
    ctAutoUpDownChanged() {
      window.ctShotInfo.autoMove.updown = this.ctAutoUpDown;
      saveShotInfo(window.ctShotInfo);
    },
    initCamera() {
      _self = this;
      this.camera.setProcessingData(false);
      this.camera.setFrameCallback(this.onFrame);
      if (!isDefined(glRenderer.glCtx) && isDefined(camDisplay)) {
        glRenderer.setupGL(camDisplay);
      }
      glRenderer.setZoom(0, 0, this.videoZoom);
      this.camera.on("frame-callback", (buf, w, h) => {
        _self.onFrame(buf, w, h);
      });
      _self.$bus.$on(EVENTS.eyeTypeShot, (eyetype, shot) => {
        _self.eyeType = eyetype;
        if (shot) {
          if (_self.isCameraReady) {
            _self.onkeydown({
              code: "Space"
            });
          }
        }
      });
      _self.setCaptureMode();
    },
    onFrame(buf, w, h) {
      if (this.showingLargeImg === true) {
        return;
      }
      glRenderer.drawFrame(w, h, glRenderer.glCtx);
      this.handleTopographyBuf(buf, w, h);
    },
    calRings() {
      this.isCalRing = false;
      let val = parseFloat(this.inputRingR);
      let offset = parseFloat(this.inputCompensate);
      if (!isNumNotNanOrInfinite(val) || val <= 0 || !isNumNotNanOrInfinite(offset)) {
        this.notify(this.$t("langcfg.txtInputValidRingDia"), "", "error");
        return;
      }
      this.isCalRing = true;
      this.$bus.$emit(EVENTS.showGlobalWatting, this.$t("langcfg.txtProccessingData"));
      let imagePath = fileSys.getMediaPath(`${getSystemTime("", "_", "")}_${this.eyeType}.jpg`);
      this.camera
        .takePhoto(imagePath)
        .then(() => {
          // 拍摄图片
          fileSys.copyFile(imagePath, fileSys.combinePath(this.imgDir, FILES.originImage));
          let result = doCalRings(
            this.shotMode === "far" ? 1 : 0,
            imagePath,
            fileSys.getCalibPath(),
            parseFloat(this.inputRingR),
            parseFloat(this.inputCompensate)
          );
          if (result) {
            fileSys.copyFile(
              fileSys.getCalibPath(FILES.calibImage),
              fileSys.combinePath(this.imgDir, FILES.calibImage)
            );
            this.notify(this.$t("langcfg.txtCalibrationSuccess"), "", "success");
          } else {
            this.notify(this.$t("langcfg.txtCalibrationFail"), "", "error");
            this.restore(false);
          }
          this.$bus.$emit(EVENTS.hideGlobalWatting);
          this.isCalRing = false;
          this.resetImgUrl();
        })
        .catch(() => {
          this.$bus.$emit(EVENTS.hideGlobalWatting);
          this.notify(this.$t("langcfg.txtFailToCapture"), "", "error");
          this.isCalRing = false;
          this.resetImgUrl();
        });
    },
    ensureBackup() {
      let backupPath = fileSys.getCalibPath("backup");
      if (!fileSys.isFileExists(backupPath)) {
        fileSys.mkdirSync(backupPath);
      }
      let files = [FILES.calib, FILES.calibRingsLarge, FILES.calibRingsLittle];
      files.forEach((file) => {
        let src = fileSys.getCalibPath(file);
        let dst = path.join(backupPath, file);
        if (!fileSys.isFileExists(dst) && fileSys.isFileExists(src)) {
          fileSys.copyFile(src, dst);
        }
      });
    },
    onRestoreClick() {
      this.confirm(
        this.$t("langcfg.txtConfirmToRestore"),
        this.$t("langcfg.txtOK"),
        this.$t("langcfg.txtCancel")
      )
        .then(() => {
          this.restore();
        })
        .catch((e) => {
          this.resetImgUrl();
        });
    },
    restore(showMsg = true) {
      let backupPath = fileSys.getCalibPath("backup");
      if (!fileSys.isFileExists(backupPath) && showMsg) {
        this.notify(this.$t("langcfg.txtRestoreFail"), "", "error");
        return;
      }
      let file = this.shotMode === "near" ? FILES.calibRingsLarge : FILES.calibRingsLittle;
      let dst = fileSys.getCalibPath(file);
      let src = path.join(backupPath, file);
      if (!fileSys.isFileExists(src) && showMsg) {
        this.notify(this.$t("langcfg.txtRestoreFail"), "", "error");
        return;
      }
      fileSys.copyFile(src, dst);
      if (showMsg) {
        this.notify(this.$t("langcfg.txtRestoreSuccess"), "", "success");
      }
      fileSys.deleteFile(fileSys.combinePath(this.imgDir, FILES.originImage));
      fileSys.deleteFile(fileSys.combinePath(this.imgDir, FILES.calibImage));
      this.resetImgUrl();
    },
    modeChanged() {
      this.setCaptureMode();
      this.resetImgUrl();
    },
    async onkeydown(e) {
      await this.removeListener();
      if (!isDefined(e) || !isDefined(_self)) return;
      if (e.preventDefault) e.preventDefault();
      if (e.code === "Space" && !this.isCalRing) {
        this.calRings();
      }
    },
    removeListener() {
      document.removeEventListener("keydown", this.onkeydown);
    },
    setCaptureMode() {
      let _mode = this.getCaptureMode();
      if (!isDefined(this.camera) || window.__capture__.debug) {
        this.$bus.$emit(EVENTS.hideGlobalWatting);
        return;
      }
      this.camera.setCaptureMode(_mode, () => {
        if (_mode === "fbut" || _mode === "ocularsurface") {
          this.camera.startBuleLight();
        } else {
          this.camera.closeBuleLight();
        }

        this.camera.setLaser(this.shotMode, () => {
          this.camera.setFilter(_mode, () => {
            this.isCameraReady = true;
            this.$bus.$emit(EVENTS.hideGlobalWatting);
          });
        });
      });
    },
    handleTopographyBuf(buf, w, h) {
      autoCnt++;
      if (!this.camera) {
        return;
      }
      if (autoCnt % 2 === 0) {
        let caledInfo = getCTCenterOffsetInfo(buf, w, h);
        this.ctXYPos = caledInfo.xyz.xypos;
        this.ctZPos = caledInfo.xyz.zpos;
        autoCnt = 0;
        this.ctCaledOffset = caledInfo.offsetxyz;
        this.drawCT();
        if (this.isCameraReady) {
          this.camera.autoCTUpDown(this.ctCaledOffset);
        }
      }
    },
    zoomPreview(ev) {
      this.videoZoom *= ev.deltaY < 0 ? 1.05 : 0.95;
      this.videoZoom = this.videoZoom > 3 ? 3 : this.videoZoom;
      this.videoZoom = this.videoZoom < 1 ? 1 : this.videoZoom;
      if (this.checkType === PAGES.topography) {
        this.drawCT();
      } else {
        let x = (ev.offsetX / ev.target.clientWidth) * 2.0 - 1.0;
        let y = 1.0 - (ev.offsetY / ev.target.clientHeight) * 2.0;
        // this.drawDE(x, y, this.videoZoom)
        glRenderer.setZoom(x, y, this.videoZoom);
      }
    },
    drawCT() {
      if (isDefined(previewIndicatrix)) {
        previewIndicatrix.drawCross(this.centerPoint.x, this.centerPoint.y, 1);
      }
      DrawCTCal.draw(
        canvasDraw,
        { xy: this.ctXYPos, z: this.ctZPos, offset: this.ctCaledOffset },
        window.ctShotInfo
      );
    },
    getCaptureMode() {
      return this.mode === PAGES.topography ? this.mode + `_${this.shotMode}` : this.mode;
    },
    exit() {
      this.$emit("closeCalibration");
    }
  },
  watch: {},
  computed: {
    ...mapGetters(["camera"]),
    videoSizePos() {
      let containerWidth = this.dispW - ASIDEWIDTH;
      let containerHeight = this.dispH;

      let ratioW = containerWidth / this.videoSize.width;
      let ratioH = containerHeight / this.videoSize.height;
      let width = 1024;
      let height = 768;
      if (ratioW < ratioH) {
        width = containerWidth;
        height = this.videoSize.height * ratioW;
      } else {
        width = this.videoSize.width * ratioH;
        height = containerHeight;
      }
      return {
        w: width,
        h: height,
        l: (containerWidth - width) / 2,
        t: (containerHeight - height) / 2 + 2
      };
    },
    ctOffsetStyle() {
      return {
        left: `${this.videoSizePos.l + this.videoSizePos.w - 300}px`,
        top: `${this.videoSizePos.t}px`,
        // color: '#f80',
        fontSize: "40px",
        textAlign: "left",
        width: "290px"
      };
    },
    ctOffsetStr() {
      return getOffsetShown(this.ctCaledOffset);
    },
    lampStyle() {
      return {
        left: `${this.videoSizePos.l / 2 + this.videoSizePos.w / 2 - 150}px`,
        top: `${this.videoSizePos.t + 10}px`,
        color: "gray",
        fontSize: "30px",
        textAlign: "left",
        width: "420px"
      };
    },
    symbolStyle() {
      return {
        left: `${this.videoSizePos.l + 5}px`,
        top: `${this.videoSizePos.t + 3}px`
      };
    },
    symbolContent() {
      return `${this.eyeType}&nbsp;
       ${this.videoZoom > 1 ? "x" + this.videoZoom._toFixed(2) + "&nbsp;" : ""}
       ${this.isRecording ? this.recordTime : ""}`;
    },
    currentModeKey() {
      return this.getCaptureMode(this.mode);
    },
    previewStyle() {
      return {
        width: `${this.videoSizePos.w}px`,
        height: `${this.videoSizePos.h - 20}px`,
        left: `${this.videoSizePos.l}px`,
        top: `${this.videoSizePos.t}px`,
        background: "#000"
      };
    },
    previewStyle1() {
      return {
        width: `${this.videoSizePos.w}px`,
        height: `${this.videoSizePos.h - 20}px`,
        left: `${this.videoSizePos.l}px`,
        top: `${this.videoSizePos.t}px`
      };
    }
  },
  beforeDestroy() {
    this.$bus.$off(TOPOGRAPHYEVENTS.showLargeImg);
    this.isCalRing = false;
    document.removeEventListener("keydown", this.onkeydown);
    glRenderer.deleteGL();
    glRenderer.glCtx = undefined;
    this.camera.closeAllLamps(() => {
      this.camera.stopUpDown();
      this.camera.off("frame-callback");
    });
    this.$bus.$off(EVENTS.eyeTypeShot);
  },
  destroyed() {}
};
</script>

<style lang="less" scoped>
.warningClass {
  z-index: 998;
  position: fixed;
  //bottom:30px;
  // /副屏统计
  bottom: 165px;
  left: 40%;
  color: rgb(176, 241, 159);
  font-size: 30px;
  transform: translateX(-50%);
  span {
    color: rgb(176, 241, 159);
  }
}
.el-button {
  width: 90%;
  font-size: @header-fs;
  color: white;
  i {
    font-size: @header-fs+30px;
  }
}
.btn-oprator {
  height: @header-fs+110px;
}
.btn-oprator:hover {
  color: @btn-hover-color;
  i {
    color: @btn-hover-color;
  }
}
.btn-oprator:active {
  background: @btn-active-color;
}
.eyetype-symbol {
  position: absolute;
  color: #f80;
  font-size: @header-fs;
  z-index: 999;
  i {
    font-size: 40px;
  }
}
.cam-window {
  border: "none";
  position: absolute;
}
.black {
  background: "#000";
}
.capture-aside {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}
.aside-content {
  width: 95%;
}
.color-normal {
  color: white;
}
.color-recording {
  color: red;
}
.border-normal :hover {
  i {
    color: @btn-hover-color;
  }
}
.border-recording {
  border: 1px solid red;
}
.border-recording:hover {
  i {
    color: red;
  }
}
.video-mask {
  position: fixed;
  left: 0px;
  top: 0px;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10;
}
.el-radio {
  height: 80px;
  line-height: 80px;
  padding: 0 20px;
}
.el-radio /deep/ .el-radio__input {
  vertical-align: -5px !important;
}
/deep/ .el-form-item__content {
  display: flex !important;
}
/deep/ .el-icon-lock {
  position: relative !important;
  right: 22px !important;
}
.form-text {
  font-size: 30px;
  color: white;
}
.split {
  width: 100%;
  height: 30px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}
.slide-fade-enter-active {
  transition: all 0.3s ease;
}
.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.3, 0.8, 1);
}
.slide-fade-enter, .slide-fade-leave-to
/* .slide-fade-leave-active 用于 2.1.8 以下版本 */ {
  transform: translateX(10px);
  opacity: 0;
}
</style>

<style lang="less">
#calibration {
  .el-checkbox .el-checkbox__label {
    font-size: 28px;
    vertical-align: middle;
  }
  .el-checkbox__inner {
    width: 30px;
    height: 30px;
  }
  .el-checkbox__inner::after {
    width: 12px;
    height: 25px;
    left: 8px;
    top: -5px;
  }
  .el-tabs--border-card {
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
  .el-tabs--border-card > .el-tabs__header .el-tabs__item.is-active {
    color: #3da13d;
    background-color: transparent;
    border-right-color: rgba(0, 255, 0, 0.4);
    border-left-color: rgba(0, 255, 0, 0.4);
  }
  .el-tabs--border-card > .el-tabs__header {
    background-color: transparent;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    margin: 0;
  }
  .el-tabs__item:hover {
    color: #3da13d;
  }
  .el-tabs__item {
    color: white;
    font-size: 23px;
  }
  .el-input__inner {
    font-size: 23px;
    line-height: 50px;
    background-color: #fff;
    height: 50px;
  }
  .el-input-group__append,
  .el-input-group__prepend {
    background-color: transparent;
    color: white;
    font-size: 30px;
    border: none; //1px solid rgba(128, 128, 128 ,0.5);
    text-align: left;
    padding: 0 10px 0 0;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }
  .btn-exit {
    line-height: 2;
    width: 90%;
    margin-top: 1px;
    margin-bottom: 20px;
  }
  .imgbox {
    position: absolute;
    z-index: 1000 !important;
    top: 32px;
    left: 0px;
  }
  .aside {
    border-left: 1px solid gray;
    padding-left: 20px;
    padding-bottom: 200px;
  }
}
</style>



// WEBPACK FOOTER //
// src/topography/components/Calibration.vue